import React, { useCallback, useEffect, useMemo, useState } from "react";

import { Button, Icon, Modal } from "semantic-ui-react";

import { isNonEmptyArray } from "@cayuse/shared-react.utils.type-checks";

import { RenderOptions } from "components/RuleConditionSelector/OptionList";
import { useDictionary } from "hooks";

import styles from "./styles.css";
import { isSearchableType } from "../utils";

const RuleModal = ({
	open,
	onClose,
	rules,
	sections,
	onAddRule,
	ruleType
}) => {
	const dictionary = useDictionary("simplifiedBuilder.components.RuleModal");
	const [ activeSection, setActiveSection ] = useState();
	const [ activeQuestion, setActiveQuestion ] = useState();

	useEffect(() => {
		setActiveQuestion(null);
	}, [ activeSection ]);

	const filteredRules = useMemo(
		() => rules.filter(rule => rule.label && rule.section === activeSection),
		[ activeSection, rules ],
	);

	const activeSectionClass = useCallback(
		section => activeSection === section ? styles.active : styles.clickable,
		[ activeSection ],
	);
	const activeQuestionClass = useCallback(
		rule => activeQuestion && activeQuestion.id === rule.id
			? styles.active
			: rule.items || isSearchableType(rule.cayuseType)
				? styles.clickable
				: "",
		[ activeQuestion ]
	);

	const handleClose = () => {
		setActiveSection(null);
		setActiveQuestion(null);
		onClose();
	};

	const onAddQuestion = rule => onAddRule(rule);

	const onSelectQuestion = rule => {
		if (isNonEmptyArray(rule.items) || isSearchableType(rule.cayuseType)) {
			setActiveQuestion(rule);
		}
	};

	const onAddAnswer = (option, index) => onAddRule({ ...activeQuestion, itemToSelect: index, itemText: option?.text });

	return (
		<Modal
			open={open}
			closeOnEscape={true}
			className={styles.ruleModal}
			dimmer={{ className: styles.overrideDimmer }}
			aria-label={ruleType === "visible" ? dictionary.displayTitle : dictionary.ignoreTitle}
		>
			<Modal.Header className="whiteOnBlue">
				<h2>{ruleType === "visible" ? dictionary.displayTitle : dictionary.ignoreTitle}</h2>
				<Icon link name="close" onClick={handleClose} className={styles.floatRight} />
			</Modal.Header>
			<Modal.Content scrolling className={styles.modalContent}>
				<div className={styles.displayRules}>
					<div className={styles.panel}>
						<div className={styles.heading}>{dictionary.sectionsHeader}</div>
						<ul className={styles.optionList}>{sections.map((section, index) => (
							<li key={section}
								className={`${styles.panelEntry} ${activeSectionClass(section)}`}
								onClick={() => setActiveSection(section)}
							>
								{index + 1}. {section}
								<div className={styles.actions}>
									<Icon name="chevron right" />
								</div>
							</li>
						))}
						</ul>
					</div>
					<div className={styles.panel}>
						<div className={styles.heading}>{dictionary.questionsHeader}</div>
						<ul className={styles.optionList}>
							{filteredRules.map(rule => (
								<li key={rule.id} className={`${styles.panelEntry} ${activeQuestionClass(rule)}`}
									onClick={() => onSelectQuestion(rule)}
								>
									<div className={styles.addButton}>
										<Button onClick={() => onAddQuestion(rule)} icon="plus" tertiary size="mini" />
									</div>
									{rule.label}
									<div className={styles.actions}>
										{(rule.items || isSearchableType(rule.cayuseType)) && <Icon link name="chevron right" />}
									</div>
								</li>
							))}
						</ul>
					</div>
					<div className={styles.panel}>
						<div className={styles.heading}>{dictionary.optionsHeader}</div>
						{activeQuestion &&
							<RenderOptions
								activeQuestion={activeQuestion}
								onAddRule={onAddQuestion}
								onAddAnswer={onAddAnswer}
							/>
						}
					</div>
				</div>
			</Modal.Content>
		</Modal>
	);
};

export default RuleModal;
